import { ThunkAction } from 'redux-thunk';
import { RootState } from '../_reducers';
import { setLoginError, setLoginInitial, setLoginSuccess } from '../_actions/login.actions';
import { LoginError } from '../_types/login';
import { OktaLoginAction } from '../_types/okta';
import { configUrl } from '../config';
import { OktaProfileFillableData } from '../_hooks/useOktaAuth';

const authRedirect = (id: number | undefined): ThunkAction<void, RootState, null, OktaLoginAction> => {
    if (id === undefined) {
        return (dispatch) => {
            dispatch(setLoginError({ message: 'Could not determine Sales Channel.' }));
        };
    }

    const url = `${configUrl}/api/${id}/ssoLoginURL?sso=okta`;

    return async (dispatch) => {
        try {
            const res = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (res.ok) {
                const resData = await res.json();
                setTimeout(() => {
                    dispatch(setLoginInitial());
                }, 1000);
                // store verifier so we can use verifier + auth code to get tokens when user returns
                sessionStorage.setItem('verifier', resData.verifier);
                window.location.href = resData.url;
            } else {
                dispatch(setLoginError({ message: 'Could not contact with Okta' }));
            }
        } catch {
            dispatch(setLoginError({ message: 'Error occurred!' }));
        }
    };
};

/**
 * Authenticate with Okta
 */
const authenticate = (id: number | undefined, code: string): ThunkAction<void, RootState, null, OktaLoginAction> => {
    const url = `${configUrl}/api/${id}/ssoAuth?sso=okta`;

    return async (dispatch) => {
        if (typeof id === 'undefined') {
            dispatch(setLoginError({ message: 'Login failed.' }));
        }

        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    authCode: code,
                    verifier: sessionStorage.getItem('verifier'),
                    salesChannelId: id,
                }),
            });
            sessionStorage.removeItem('verifier');

            if (!res.ok) {
                const resData: LoginError = (await res.json()).error;
                dispatch(setLoginError(resData));
                return;
            }

            const resData = await res.json();
            // dispatch(setLoginSuccess(resData));
            return resData;
        } catch (err) {
            dispatch(setLoginError({ message: 'Error occurred!' }));
            throw err;
        }
    };
};

/**
 * Sign in / Register to Pogoseat
 */
const signIn = (
    id: number | undefined,
    /* eslint-disable  @typescript-eslint/no-explicit-any */
    ssoProfile: Record<string, any>,
    data: OktaProfileFillableData,
): ThunkAction<void, RootState, null, OktaLoginAction> => {
    const url = `${configUrl}/api/${id}/ssoLogin?sso=okta`;

    return async (dispatch) => {
        if (typeof id === 'undefined') {
            dispatch(setLoginError({ message: 'Login failed.' }));
        }

        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'x-id-token': ssoProfile.token.id_token,
                    'x-access-token': ssoProfile.token.access_token,
                    'x-refresh-token': ssoProfile.token.refresh_token,
                },
                body: JSON.stringify({
                    salesChannelId: id,
                    messageChannelType: 'twilio',
                    source: sessionStorage.getItem('source') || 'WEB',
                    campaign: sessionStorage.getItem('campaign'),
                    referrer: sessionStorage.getItem('referrer'),
                    optedInInformational: data?.optInStatus,
                    ...data,
                }),
            });

            if (!res.ok) {
                const resData: LoginError = (await res.json()).error;
                dispatch(setLoginError(resData));
                return;
            }

            const resData = await res.json();
            dispatch(setLoginSuccess(resData));
            return resData;
        } catch (err) {
            dispatch(setLoginError({ message: 'Error occurred!' }));
        }
    };
};

const ballparkSignIn = (
    id: number | undefined,
    data: { mlbamOkta?: string | null; mlbEmail?: string | null },
): ThunkAction<void, RootState, null, OktaLoginAction> => {
    const url = `${configUrl}/api/${id}/ballparkLogin?sso=ballpark`;

    return async (dispatch) => {
        if (typeof id === 'undefined') {
            dispatch(setLoginError({ message: 'Login failed.' }));
        }

        try {
            const res = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    salesChannelId: id,
                    source: sessionStorage.getItem('source') || 'WEB',
                    campaign: sessionStorage.getItem('campaign'),
                    ...data,
                }),
            });

            if (!res.ok) {
                const resData: LoginError = (await res.json()).error;
                dispatch(setLoginError(resData));
                return;
            }

            const resData = await res.json();
            dispatch(setLoginSuccess(resData));
            return resData;
        } catch (err) {
            dispatch(setLoginError({ message: 'Error occurred!' }));
        }
    };
};

export const okta = {
    authRedirect,
    authenticate,
    signIn,
    ballparkSignIn,
};
